import { SeatingChart } from "./SeatingChart";
import { NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import type { ClassState } from "../../shared/api/types";
import { image } from "../../shared/assets/manifest";
import { growthInfo, studentFaceSrc } from "../../shared/growth";
import { Button } from "../../shared/ui/Button";
import { RaceTrackList } from "../../shared/ui/CuteDecor";
import { EmptyState } from "../../shared/ui/EmptyState";
import { errorMessage, useToast } from "../../shared/ui/ToastProvider";
import { canWrite, groupScores, signed, useApplyGroupPoints } from "./actions";

const CLASSROOM_SHORTCUTS = [
  { label: "计时器", icon: image.classroomTimer, hash: "#timer" },
  { label: "随机点名", icon: image.shortcutRandom, hash: "#random" },
  { label: "小组竞赛", icon: image.classroomGroup, hash: "#race" },
  { label: "奖品兑换", icon: image.shortcutReward, to: "/rewards" }
] as const;

function ToolTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <span className="tool-title">
      <img src={icon} alt="" />
      {title}
    </span>
  );
}


function RandomPicker({
  state,
  resetToken
}: {
  state: ClassState;
  resetToken: number;
}) {
  const toast = useToast();
  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const [history, setHistory] = useState<{ id: number; name: string }[]>([]);
  const [flicker, setFlicker] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<number | null>(null);

  const reset = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    setPickedIds([]);
    setHistory([]);
    setFlicker(null);
    setAnimating(false);
  };

  useEffect(() => {
    reset();
  }, [state.classId, resetToken]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    },
    []
  );

  const remaining = state.students.filter((student) => !pickedIds.includes(student.id)).length;

  const pick = () => {
    if (animating) return;
    const pool = state.students.filter((student) => !pickedIds.includes(student.id));
    if (!pool.length) {
      toast("所有学生都已点过", "warning");
      return;
    }
    setAnimating(true);
    let ticks = 0;
    timerRef.current = window.setInterval(() => {
      const sample = pool[Math.floor(Math.random() * pool.length)];
      setFlicker(sample.name);
      ticks += 1;
      if (ticks >= 12) {
        if (timerRef.current) window.clearInterval(timerRef.current);
        const winner = pool[Math.floor(Math.random() * pool.length)];
        setFlicker(winner.name);
        setPickedIds((current) => [...current, winner.id]);
        setHistory((current) => [{ id: winner.id, name: winner.name }, ...current].slice(0, 6));
        setAnimating(false);
      }
    }, 70);
  };

  return (
    <article className="surface-panel random-panel" id="random">
      <div className="panel-heading">
        <h3>
          <ToolTitle icon={image.classroomRandom} title="随机点名" />
        </h3>
        <Button variant="secondary" size="small" type="button" onClick={reset}>
          重置
        </Button>
      </div>
      <div className={`random-pick-display ${animating ? "spinning" : ""}`}>
        <img src={image.classroomRandom} alt="" />
        <strong>{flicker || "点击开始点名"}</strong>
        <span>
          已点 {pickedIds.length} 人 · 剩余 {remaining} 人
        </span>
      </div>
      <Button variant="primary" type="button" disabled={animating || !state.students.length} onClick={pick}>
        开始点名
      </Button>
      {history.length ? (
        <div className="picker-history">
          {history.map((item, index) => (
            <span className="small-pill" key={`${item.id}-${index}`}>
              {item.name}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}

const TIMER_PRESETS = [60, 180, 300, 600];

function ClassroomTimer() {
  const toast = useToast();
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const stop = () => {
    setRunning(false);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
  };

  const setFromInputs = (nextMinutes: number, nextSeconds: number) => {
    stop();
    const total = Math.min(7200, Math.max(0, nextMinutes * 60 + nextSeconds));
    setMinutes(Math.floor(total / 60));
    setSeconds(total % 60);
    setTotalSeconds(total);
    setRemaining(total);
  };

  const start = () => {
    const total = remaining || Math.min(7200, Math.max(0, minutes * 60 + seconds));
    if (!total) {
      toast("请先设置计时时长", "error");
      return;
    }
    setTotalSeconds(total);
    setRemaining(total);
    setRunning(true);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(() => {
      setRemaining((current) => {
        if (current <= 1) {
          if (intervalRef.current) window.clearInterval(intervalRef.current);
          setRunning(false);
          toast("计时结束", "success");
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  };

  const reset = () => {
    stop();
    setRemaining(totalSeconds || minutes * 60 + seconds);
  };

  useEffect(
    () => () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    },
    []
  );

  const progress = totalSeconds ? (remaining / totalSeconds) * 100 : 0;
  const format = (value: number) =>
    `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;

  return (
    <article className="surface-panel timer-panel" id="timer">
      <div className="panel-heading">
        <h3>
          <ToolTitle icon={image.classroomTimer} title="课堂计时器" />
        </h3>
      </div>
      <img className="timer-sun" src={image.classroomTimer} alt="" />
      <div className={`timer-display ${running ? "running" : remaining === 0 && totalSeconds ? "finished" : ""}`}>
        {format(remaining || totalSeconds || minutes * 60 + seconds)}
      </div>
      <div className={`timer-progress ${running ? "running" : remaining === 0 && totalSeconds ? "finished" : ""}`}>
        <div className="timer-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="inline-form">
        <label>
          分
          <input
            type="number"
            min={0}
            max={120}
            value={minutes}
            disabled={running}
            onChange={(event) => setFromInputs(Number(event.target.value) || 0, seconds)}
          />
        </label>
        <label>
          秒
          <input
            type="number"
            min={0}
            max={59}
            value={seconds}
            disabled={running}
            onChange={(event) => setFromInputs(minutes, Number(event.target.value) || 0)}
          />
        </label>
      </div>
      <div className="tab-row">
        {TIMER_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className="tab-pill small"
            disabled={running}
            onClick={() => setFromInputs(Math.floor(preset / 60), preset % 60)}
          >
            {preset / 60} 分钟
          </button>
        ))}
      </div>
      <div className="panel-actions" style={{ marginTop: 10 }}>
        {!running ? (
          <Button variant="primary" type="button" onClick={start}>
            开始
          </Button>
        ) : (
          <Button variant="secondary" type="button" onClick={stop}>
            暂停
          </Button>
        )}
        <Button variant="secondary" type="button" onClick={reset}>
          重置
        </Button>
      </div>
    </article>
  );
}

function GroupCompetition({ state }: { state: ClassState }) {
  const writable = canWrite(state);
  const toast = useToast();
  const applyGroupPoints = useApplyGroupPoints();
  const ranked = groupScores(state);

  const score = (groupId: number, amount: number) => {
    if (!writable) return;
    applyGroupPoints.mutate(
      { groupId, amount, reason: "课堂竞赛", category: "manners" },
      { onError: (error) => toast(errorMessage(error, "小组记分失败"), "error") }
    );
  };

  return (
    <article className="surface-panel" id="race">
      <div className="panel-heading">
        <h3>
          <ToolTitle icon={image.classroomGroup} title="小组竞赛记分" />
        </h3>
      </div>
      {ranked.length ? (
        <RaceTrackList
          groups={ranked.map((group) => ({
            id: group.id,
            name: group.name,
            total: group.total,
            color: group.color,
            count: group.memberCount,
            avg: group.avg
          }))}
          actions={
            writable
              ? (group) => (
                  <>
                    <Button variant="secondary" size="small" type="button" onClick={() => score(group.id, 1)}>
                      +1
                    </Button>
                    <Button variant="secondary" size="small" type="button" onClick={() => score(group.id, 3)}>
                      +3
                    </Button>
                    <Button variant="secondary" size="small" type="button" onClick={() => score(group.id, -1)}>
                      -1
                    </Button>
                  </>
                )
              : undefined
          }
        />
      ) : (
        <EmptyState title="还没有小组" detail="先在小组页创建小组。" />
      )}
    </article>
  );
}

export function ClassroomToolsPage({ state }: { state: ClassState }) {

  return (
    <div className="classroom-tools-page">
      <div className="classroom-shortcut-bar">
        {CLASSROOM_SHORTCUTS.map((item) =>
          "to" in item ? (
            <NavLink key={item.label} className="classroom-shortcut" to={item.to}>
              <img src={item.icon} alt="" />
              {item.label}
            </NavLink>
          ) : (
            <a key={item.label} className="classroom-shortcut" href={item.hash}>
              <img src={item.icon} alt="" />
              {item.label}
            </a>
          )
        )}
      </div>
      <div className="dashboard-grid classroom-tools-grid">
        <SeatingChart key={`${state.organizationId}-${state.classId}`} state={state} />
        <RandomPicker state={state} resetToken={0} />
        <ClassroomTimer />
        <GroupCompetition state={state} />
      </div>
    </div>
  );
}
